// Contract of the Power admin route for the deployment profile: the platform environment this
// deployment runs in, which regulatory ecosystems it serves, with the OPIN participant role where
// OPIN is on, and where its gateway serves the application token endpoint. The deploy layer
// states each of them; the admin reads them instead of presuming OFB, a transmitter, a local
// environment or a token host. The environment, the role and the token host are the runtime
// contract's own vocabularies, read through its browser-safe entry points.
//
// The structs are closed contracts: decode them with `{ onExcessProperty: 'error' }`, so a member
// the contract does not define is refused rather than dropped.
import { Schema } from 'effect';
import { OpinParticipantRole } from '@finnest/runtime-cd-contract/opin-participant-role';
import { opinConsumesData } from '@finnest/runtime-cd-contract/opin-participant-role-policy';
import { TokenEndpointHost } from '@finnest/runtime-cd-contract/token-endpoint-host';
import { SliceEnvironment } from '@finnest/runtime-cd-contract/platform-fields';

const Disabled = Schema.Struct({ status: Schema.Literal('disabled') });

/** Whether the deployment serves Open Finance Brasil. */
export const OfbDeploymentView = Schema.Union([
  Schema.Struct({ status: Schema.Literal('enabled') }),
  Disabled,
]).annotate({
  title: 'PowerAdminOfbDeployment',
  description: 'Whether this deployment serves the OFB ecosystem.',
});
export type OfbDeploymentView = typeof OfbDeploymentView.Type;

/** Whether the deployment serves Open Insurance Brasil, and in which participant role. */
export const OpinDeploymentView = Schema.Union([
  Schema.Struct({ status: Schema.Literal('enabled'), participantRole: OpinParticipantRole }),
  Disabled,
]).annotate({
  title: 'PowerAdminOpinDeployment',
  description: 'Whether this deployment serves the OPIN ecosystem, and its participant role.',
});
export type OpinDeploymentView = typeof OpinDeploymentView.Type;

export const PowerDeploymentProfile = Schema.Struct({
  environment: SliceEnvironment,
  ecosystems: Schema.Struct({ ofb: OfbDeploymentView, opin: OpinDeploymentView }),
  /** Where the gateway serves the application token endpoint, as the deploy renders it. */
  tokenEndpointHost: TokenEndpointHost,
}).annotate({
  title: 'PowerAdminDeploymentProfile',
  description:
    'The environment of this deployment, the ecosystems it serves and where it serves the application token endpoint.',
});
export type PowerDeploymentProfile = typeof PowerDeploymentProfile.Type;

/** Whether the deployment serves the OPIN receiver, the API an OPIN application calls. */
export const servesOpinReceiver = (profile: PowerDeploymentProfile): boolean =>
  profile.ecosystems.opin.status === 'enabled' &&
  opinConsumesData(profile.ecosystems.opin.participantRole);
